// #706 — multi-valued brand picker for employee specialties. Same closed
// popover as ManufacturerCombobox; clicking toggles membership and the
// popover STAYS OPEN (multi-select). Trigger shows the selected names.

import * as React from 'react';
import { cn } from '@/lib/utils';
import { Button, ButtonArrow } from '@marine/ui/button';
import {
  Command,
  CommandCheck,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@marine/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@marine/ui/popover';
import { ScrollArea } from '@marine/ui/scroll-area';
import { useCanonicalManufacturers } from '@/hooks/useCanonicalManufacturers';

export interface ManufacturerMultiComboboxProps {
  value: string[];
  onChange: (ids: string[]) => void;
  id?: string;
  disabled?: boolean;
  placeholder?: string;
}

export function ManufacturerMultiCombobox({
  value,
  onChange,
  id,
  disabled = false,
  placeholder,
}: ManufacturerMultiComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const { data, isLoading } = useCanonicalManufacturers('boat');
  const options = data ?? [];

  const trimmed = search.trim().toLowerCase();
  const filtered =
    trimmed === '' ? options : options.filter((o) => o.name.toLowerCase().includes(trimmed));
  const selectedNames = options.filter((o) => value.includes(o.id)).map((o) => o.name);
  const triggerLabel =
    selectedNames.length > 0 ? selectedNames.join(', ') : (placeholder ?? 'No brand specialties');

  const toggle = (nextId: string) => {
    onChange(value.includes(nextId) ? value.filter((v) => v !== nextId) : [...value, nextId]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          mode="input"
          placeholder={selectedNames.length === 0}
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between"
        >
          <span className={cn('truncate', selectedNames.length === 0 && 'text-muted-foreground')}>
            {isLoading && selectedNames.length === 0 ? 'Loading…' : triggerLabel}
          </span>
          <ButtonArrow />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search brands…"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <ScrollArea viewportClassName="max-h-[260px] [&>div]:block!">
              <CommandEmpty>{isLoading ? 'Loading…' : 'No brands found.'}</CommandEmpty>
              <CommandGroup>
                {filtered.map((opt) => (
                  <CommandItem key={opt.id} value={opt.id} onSelect={() => toggle(opt.id)}>
                    <span className="truncate">{opt.name}</span>
                    <CommandCheck
                      className={cn(value.includes(opt.id) ? 'opacity-100' : 'opacity-0')}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
